// src/utils/storage.js
export class Storage {
  static SESSION_KEY = 'govsky_session';
  static PERSISTENT_SESSION_KEY = 'govsky_persistent_session';

  /**
   * Store session data securely
   * @param {Object} session - Session data containing JWT tokens (NOT passwords)
   * @param {boolean} remember - Whether to persist across browser sessions
   */
  static setSession(session, remember = true) {
    // Validate session has required fields
    if (!session?.accessJwt || !session?.refreshJwt || !session?.did) {
      return;
    }

    const sessionData = {
      did: session.did,
      handle: session.handle,
      email: session.email,
      accessJwt: session.accessJwt,
      refreshJwt: session.refreshJwt,
      avatar: session.avatar,
      displayName: session.displayName,
      timestamp: new Date().getTime(),
      remember
    };

    // Always set in sessionStorage for current tab
    sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(sessionData));

    // If remember is true, also store in localStorage for persistence
    if (remember) {
      localStorage.setItem(this.PERSISTENT_SESSION_KEY, JSON.stringify(sessionData));
    } else {
      localStorage.removeItem(this.PERSISTENT_SESSION_KEY);
    }
  }

  /**
   * Retrieve stored session data
   * @returns {Object|null} Session data or null if not found/expired
   */
  static getSession() {
    try {
      // First try sessionStorage (current tab)
      const sessionData = sessionStorage.getItem(this.SESSION_KEY);
      if (sessionData) {
        const data = JSON.parse(sessionData);
        // Validate required fields
        if (data?.accessJwt && data?.refreshJwt && data?.did) {
          return data;
        }
      }

      // Then try localStorage (persistent)
      const persistentData = localStorage.getItem(this.PERSISTENT_SESSION_KEY);
      if (persistentData) {
        const data = JSON.parse(persistentData);
        
        // Validate required fields
        if (!data?.accessJwt || !data?.refreshJwt || !data?.did) {
          this.clearSession();
          return null;
        }
        
        // Check if session is not too old (30 days for refresh token)
        const thirtyDays = 30 * 24 * 60 * 60 * 1000;
        if (new Date().getTime() - data.timestamp < thirtyDays) {
          // Copy to sessionStorage for current tab
          sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(data));
          return data;
        } else {
          // Clear expired session
          this.clearSession();
        }
      }
    } catch (error) {
      this.clearSession();
    }
    return null;
  }

  /**
   * Clear all stored session data
   */
  static clearSession() {
    sessionStorage.removeItem(this.SESSION_KEY);
    localStorage.removeItem(this.PERSISTENT_SESSION_KEY);
  }

  /**
   * Update session with refreshed tokens
   * @param {Object} newTokens - New access/refresh tokens
   */
  static updateTokens(newTokens) {
    const session = this.getSession();
    if (session) {
      session.accessJwt = newTokens.accessJwt || session.accessJwt;
      session.refreshJwt = newTokens.refreshJwt || session.refreshJwt;
      session.timestamp = new Date().getTime();
      this.setSession(session, session.remember);
    }
  }
}